/**
 * Plan upgrade / renewal dialog. Composes PlanUpgradeDialogTemplate with config-driven content.
 * Variants: usage_limit_exceeded (progress bar), plan_expired, no_plan.
 * Architectural Context: Feature dialog. Mounted by GlobalDialogs; opened via usePlanUsageGuard callbacks.
 *
 * @param open - Visibility
 * @param onOpenChange - Close handler
 * @param config - Variant + metric/limit/used for usage_limit_exceeded
 * @param origin - GTM origin
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Sparkles, RefreshCw } from 'lucide-react';
import { IconWrapper } from '../../ui/atoms';
import { PlanUpgradeDialogTemplate } from '../../ui/templates/PlanUpgradeDialogTemplate';
import {
  getPlanUpgradeDialogProps,
  type PlanUpgradeDialogConfig,
  type PlanUpgradeDialogVariant,
  type PlanUpgradeDialogIconKey,
} from './PlanUpgradeDialog.config';

const CTA_ICONS: Record<PlanUpgradeDialogIconKey, React.ReactNode> = {
  sparkles: (
    <IconWrapper size="sm" color="inherit" ariaHidden>
      <Sparkles />
    </IconWrapper>
  ),
  refresh: (
    <IconWrapper size="sm" color="inherit" ariaHidden>
      <RefreshCw />
    </IconWrapper>
  ),
};

export interface PlanUpgradeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  config: PlanUpgradeDialogConfig;
  origin?: string;
}

const TITLE_ID_BY_VARIANT: Record<PlanUpgradeDialogVariant, string> = {
  usage_limit_exceeded: 'plan-upgrade-dialog-usage',
  plan_expired: 'plan-upgrade-dialog-expired',
  no_plan: 'plan-upgrade-dialog-no-plan',
};

export const PlanUpgradeDialog: React.FC<PlanUpgradeDialogProps> = ({
  open,
  onOpenChange,
  config,
  origin = 'usage_limit',
}) => {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const props = getPlanUpgradeDialogProps(config, t);
  const primaryCtaIcon = CTA_ICONS[props.ctaIconKey];

  const handlePrimaryClick = () => {
    // Route existing users to the in-page "Choose a plan" view on /billing.
    navigate('/billing');
    onOpenChange(false);
  };

  return (
    <PlanUpgradeDialogTemplate
      open={open}
      onOpenChange={onOpenChange}
      title={props.title}
      description={props.description}
      progress={'progress' in props ? props.progress : undefined}
      primaryCtaLabel={props.ctaLabel}
      primaryCtaIcon={primaryCtaIcon}
      onPrimaryClick={handlePrimaryClick}
      origin={origin || 'plan_upgrade_dialog'}
      titleId={TITLE_ID_BY_VARIANT[config.variant]}
      maxWidth={props.maxWidth}
    />
  );
};
